import { user } from 'ampersand-app'
import request from '@/modules/base/util/request'
import accountConfig from '@/modules/account/config'
import lang from '@/lang'

import { toast } from '@/modules/base/components/toast/toastConfig'
import { MenuPositions, MenuTypes } from '@/modules/layout/constants'
import { cancelUploads } from '@/redux/ducks/document/upload'
import { terminateWorker } from '@/redux/ducks/worker'
import { hideLoader } from '@/redux/ducks/tenant/selection'
import { navigate } from '@/modules/layout/util/useInterceptedNavigate'
import { modalConfirmation } from '@/modules/_helpers/openModal'
import { setActiveTagFilter } from '@/redux/ducks/document/list'
import { updateSubscriptionDetails } from '@/redux/ducks/subscription/details'
import { reset as resetWizard } from '@/redux/ducks/wizard/config'
import { resetFiltersForAllLists } from '@/redux/ducks/lists'
import { fetchAccountSettingsAndTaxOfficeExp } from '@/redux/ducks/taxForms/settings'
import { captureBootstrapError } from '@/modules/account/model/login-bootstrap'

// actions
const ACTION_PREFIX = 'user/info'
const SET_DATA = `${ACTION_PREFIX}/setData`
const UPDATE_DATA = `${ACTION_PREFIX}/updateData`
const SET_ERROR = `${ACTION_PREFIX}/setError`

// reducer
const initialState = {
  firstName: '',
  lastName: '',
  email: '',
  id: null,
  hasConfirmedEmail: null,
  // DO-13065: derived by the backend, timeless — "may work" (confirmed OR website-configurator user).
  // Functional locks (invoice lock, checkout, upgrade) read this; the cosmetic hints stay on hasConfirmedEmail.
  hasEmailAccess: null,
  hasPasswordDefined: null,
  tenants: [],
  tenantUserId: null,
  activeTenant: {},
  createdAt: null,
  registeredAt: null,
  registrationClient: null,
  roles: [],
  options: {
    hideForbiddenMenuItems: false,
  },
  subscription: '',
  tenantId: null,
  finishedOnboarding: true,
  gaClientIdNeeded: false,
  isAdminLogin: false,
  isOwnerTaxConsultant: false,
  error: null,
  avatarImageUrl: '',
  loggedIn: false,
  token: null,
  layoutSettings: { menu: { position: MenuPositions.left, type: MenuTypes.comfortable } },
  csatTrackings: [],
  loginInProgress: false,
}

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SET_DATA:
      const { data } = action
      return Object.assign({}, state, data, { error: null })
    case UPDATE_DATA:
      return { ...state, ...action.data }
    case SET_ERROR:
      const { error } = action
      return Object.assign({}, state, { error })
    default:
      return state
  }
};

export const updateData = (data) => {
  return {
    type: SET_DATA,
    data,
  }
}

export const patchData = (data) => {
  return {
    type: UPDATE_DATA,
    data,
  }
}

export const setError = (error) => {
  return {
    type: SET_ERROR,
    error,
  }
}

export const fetchData = (callBack = () => {}) => {
  return (dispatch) => {
    return user.getUserInfo()
      .then(({ body: { data: { userRoles: roles, ...rest } } }) => {
        // backend returns the field as `userRoles` to keep the legacy `roles` key for the mobile apps
        const data = { ...rest, roles }
        dispatch(updateData(data))
        user.setUserInfo(data, false)
        callBack(data)
      })
      .catch((error) => {
        // without roles every protected route looks forbidden, so make this visible in APM
        captureBootstrapError('userInfo', error)
        dispatch(setError(error))
      })
  }
}

export const setTenantName = (tenantName) => {
  return (dispatch) => {
    return request(accountConfig.endpoints.putTenantName, { auth: true, method: 'PUT', data: { tenantName } })
      .then(() => {
        dispatch(fetchData())
        toast.showToast({ type: 'info', message: lang.tenant.tenantNameUpdated })
      })
      .then(({ body: { data } }) => {
        dispatch(updateData(data))
      })
      .catch((error) => {
        dispatch(setError(error))
      })
  }
}

export const switchTenant = (tenant, cb) => {
  const confirm = (dispatch, isDemo) => {
    dispatch(cancelUploads())
    dispatch(terminateWorker())
    dispatch(setActiveTagFilter({ date: [], tags: [] }))

    return request(accountConfig.endpoints.switch, { auth: true, method: 'POST', data: { tenantId: parseInt(tenant.tenantId) }, isConcurrent: true })
      .then(async(response) => {
        return user.switch(response.body)
      })
      .then((result) => {
        // switch() delegates to login(), which never rejects and decides the navigation itself
        // when the bootstrap failed. Running the follow-up would navigate on top of that and
        // leave the user in the new tenant without loaded data.
        if (!result?.success) return { success: false }

        // Reset wizard state to allow onboarding modal to show for new tenant
        dispatch(resetWizard())
        // Force update subscription details to trigger onboarding check for new tenant
        updateSubscriptionDetails(true, true)
        // reset saved state for all lists
        dispatch(resetFiltersForAllLists())
        // fetch the current tax settings
        dispatch(fetchAccountSettingsAndTaxOfficeExp())

        return { success: true }
      })
      .then((result) => {
        if (!result?.success) return
        // we have to force the reload as we need to be sure all i initialized again.
        // Navigate to firstSteps if demo tenant, otherwise to root
        const targetRoute = isDemo ? '/firstSteps' : '/'
        navigate(targetRoute)
        // navigateToCurrentPage()
        if (cb) cb(null, tenant)
      })
      .catch(error => {
        if (cb) cb(error, tenant)
      })
  }

  return async(dispatch, state) => {
    const pending = state().document.upload.pending
    const calculating = state().document.upload.calculating

    // Check if the tenant being switched to is a demo tenant
    const isDemo = tenant?.isDemo || state().user.info.tenants?.find(t => t.tenantId === tenant.tenantId)?.isDemo || false

    const hasUploads = pending.filter(({ running, error }) => !!running && !error).length || calculating.filter(({ error }) => !error).length
    if (hasUploads) {
      return modalConfirmation(() => {
        return confirm(dispatch, isDemo)
      }, {
        onAbort: () => {
          dispatch(hideLoader())
        },
        title: lang.documents.upload.interruptPendingUploadsTitle,
        description: lang.documents.upload.interruptPendingUploadsDescription,
      })
    } else {
      return confirm(dispatch, isDemo)
    }
  }
}
